import * as React from 'react'

import { cn } from '@/registry/reapit/lib/utils'

// DS: Card background = --card (#FFFFFF), border = --border (#E5E9ED),
// border-radius = --radius-lg (8 px via rounded-lg), padding = 24 px (p-6).
// No shadow in DS — shadow-sm kept as Reapit extension for elevation clarity.

function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card"
      className={cn('flex flex-col gap-6 rounded-lg border bg-card py-6 text-card-foreground shadow-sm', className)}
      {...props}
    />
  )
}

function CardHeader({
  className,
  horizontal = false,
  ...props
}: React.ComponentProps<'div'> & { horizontal?: boolean }) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        horizontal
          ? 'flex flex-row items-center justify-between space-y-0 pb-2 px-6'
          : '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
        className,
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  // DS: Card title = DS "L" text style — 20 px semi-bold
  return <div data-slot="card-title" className={cn('text-lg leading-7 font-semibold', className)} {...props} />
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  // DS: supporting text = DS "S" body — 14 px regular, muted foreground
  return (
    <div data-slot="card-description" className={cn('text-sm leading-5 text-muted-foreground', className)} {...props} />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  // DS: content padding matches card horizontal gutter = px-6
  return <div data-slot="card-content" className={cn('px-6', className)} {...props} />
}

function CardFooter({ className, divided = false, ...props }: React.ComponentProps<'div'> & { divided?: boolean }) {
  // DS: footer at p-6; divided variant adds top border separator
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 [.border-t]:pt-6', divided && 'justify-between border-t pt-4', className)}
      {...props}
    />
  )
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent }
